import React from 'react';
import { Typography, Alert, Card, Tag, Descriptions, Space, Button, Row, Col, Divider } from 'antd';
import { CopyOutlined, DownloadOutlined, ThunderboltOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface PrimerInfo {
  sequence: string;
  length: number;
  tm: number;
  gc_content: number;
  is_forward: boolean;
  penalty_score: number;
  dg: number;
}

interface SequenceResult {
  sequence_id: string;
  sequence_length: number;
  success: boolean;
  message: string;
  forward_primer?: PrimerInfo;
  reverse_primer?: PrimerInfo;
  tm_difference?: number;
  amplicon_length?: number;
}

interface UnifiedData {
  total_sequences: number;
  successful_count: number;
  failed_count: number;
  results: Array<{
    sequence_id: string;
    result: {
      success: boolean;
      message: string;
      forward_primer?: PrimerInfo;
      reverse_primer?: PrimerInfo;
      tm_difference?: number;
      amplicon_length?: number;
      sequence_length?: number;
    };
    success: boolean;
    error?: string;
  }>;
  artifacts?: any;
  meta?: any;
}

interface PrimerDesignResultProps {
  taskResult?: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const PrimerDesignResult: React.FC<PrimerDesignResultProps> = ({ 
  taskResult, 
  data, 
  title = "PCR引物设计结果", 
  showTitle = true 
}) => {

  // 安全渲染函数
  const safeRender = (value: any, fallback: string = 'N/A'): string => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number') return value.toString();
    return String(value);
  };

  // 安全数值渲染函数
  const safeNumber = (value: any, decimals?: number): string => {
    if (typeof value === 'number' && !isNaN(value)) {
      return decimals !== undefined ? value.toFixed(decimals) : value.toString();
    }
    return 'N/A';
  };

  // 解析统一格式数据
  const parseUnifiedData = (): UnifiedData | null => {
    // 尝试从不同位置获取统一格式数据
    let unifiedData = null;
    
    if (taskResult?.result && typeof taskResult.result === 'object' && 'total_sequences' in taskResult.result) {
      unifiedData = taskResult.result;
    } else if (data && typeof data === 'object' && 'total_sequences' in data) {
      unifiedData = data;
    } else if (taskResult && typeof taskResult === 'object' && 'total_sequences' in taskResult) {
      unifiedData = taskResult;
    }

    return unifiedData;
  };

  // 解析传统格式数据
  const parseLegacyData = (): SequenceResult[] => {
    // 尝试从不同位置获取传统格式数据
    if (taskResult?.results && Array.isArray(taskResult.results)) {
      return taskResult.results;
    }
    if (data?.results && Array.isArray(data.results)) {
      return data.results;
    }
    return [];
  };

  // 复制到剪贴板
  const copyToClipboard = (text: string, type: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        // 可以添加消息提示
      });
    }
  };

  // 下载所有引物
  const downloadAllPrimers = (results: any[]) => {
    const content = results.map((result, index) => {
      const seqResult = result.result || result;
      if (!seqResult.success || !seqResult.forward_primer || !seqResult.reverse_primer) {
        return '';
      }
      
      const seqId = result.sequence_id || `sequence_${index + 1}`;
      return [
        `>${seqId}_forward`,
        seqResult.forward_primer.sequence,
        `>${seqId}_reverse`,
        seqResult.reverse_primer.sequence
      ].join('\n');
    }).filter(Boolean).join('\n\n');
    
    if (content) {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pcr_primers.fasta';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // 渲染引物卡片
  const renderPrimerCard = (primer: PrimerInfo, title: string, color: string) => (
    <Card 
      title={
        <Space>
          <ThunderboltOutlined style={{ color }} />
          {title}
        </Space>
      }
      size="small"
      extra={
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyToClipboard(primer.sequence, title)}
        >
          复制序列
        </Button>
      }
    >
      <Descriptions column={1} size="small">
        <Descriptions.Item label="序列">
          <Text code style={{ fontSize: '14px', wordBreak: 'break-all' }}>
            {primer.sequence}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="长度">
          {safeNumber(primer.length)} bp
        </Descriptions.Item>
        <Descriptions.Item label="Tm值">
          <Tag color={typeof primer.tm === 'number' && primer.tm >= 58 && primer.tm <= 65 ? 'green' : 'orange'}>
            {safeNumber(primer.tm, 1)}°C
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="GC含量">
          <Tag color={typeof primer.gc_content === 'number' && primer.gc_content >= 0.4 && primer.gc_content <= 0.6 ? 'green' : 'orange'}>
            {typeof primer.gc_content === 'number' ? safeNumber(primer.gc_content * 100, 1) : 'N/A'}%
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="惩罚得分">
          <Tag color={typeof primer.penalty_score === 'number' && primer.penalty_score < 5 ? 'green' : typeof primer.penalty_score === 'number' && primer.penalty_score < 10 ? 'orange' : 'red'}>
            {safeNumber(primer.penalty_score, 2)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="自由能">
          {safeNumber(primer.dg, 2)} kcal/mol
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  const unifiedData = parseUnifiedData();
  const legacyResults = parseLegacyData();

  // 处理统一格式数据
  if (unifiedData) {
    const { total_sequences, successful_count, failed_count, results } = unifiedData;
    
    return (
      <div>
        {showTitle && <Title level={4}>{title}</Title>}
        
        <Alert
          message={`引物设计完成`}
          description={`总共处理 ${total_sequences} 个序列，成功 ${successful_count} 个，失败 ${failed_count} 个`}
          type={successful_count > 0 ? 'success' : 'error'}
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {results.map((item, index) => {
          const seqResult = item.result || {};
          const seqId = item.sequence_id || `sequence_${index + 1}`;
          const success = item.success && seqResult.success;
          
          return (
            <Card 
              key={seqId}
              title={`序列: ${seqId} (${seqResult.sequence_length || 0}bp)`}
              style={{ marginBottom: 16 }}
              size="small"
            >
              {success ? (
                <div>
                  <Row gutter={16}>
                    <Col span={12}>
                      {seqResult.forward_primer && renderPrimerCard(seqResult.forward_primer, '正向引物 (Forward)', '#1890ff')}
                    </Col>
                    <Col span={12}>
                      {seqResult.reverse_primer && renderPrimerCard(seqResult.reverse_primer, '反向引物 (Reverse)', '#52c41a')}
                    </Col>
                  </Row>

                  <Card title="引物对评估" style={{ marginTop: 16 }} size="small">
                    <Descriptions column={2}>
                      <Descriptions.Item label="Tm差异">
                        <Tag color={typeof seqResult.tm_difference === 'number' && seqResult.tm_difference < 3 ? 'green' : 'orange'}>
                          {safeNumber(seqResult.tm_difference, 1)}°C
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="扩增产物长度">
                        {safeNumber(seqResult.amplicon_length)} bp
                      </Descriptions.Item>
                      <Descriptions.Item label="引物对匹配度">
                        <Tag color="green">优秀</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="建议PCR条件">
                        退火温度: {seqResult.forward_primer && seqResult.reverse_primer && 
                          typeof seqResult.forward_primer.tm === 'number' && typeof seqResult.reverse_primer.tm === 'number' ? 
                          safeNumber(Math.min(seqResult.forward_primer.tm, seqResult.reverse_primer.tm) - 5, 0) : 'N/A'}°C
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </div>
              ) : (
                <Alert
                  message="引物设计失败"
                  description={safeRender(item.error || seqResult.message, '未知错误')}
                  type="error"
                  showIcon
                />
              )}
            </Card>
          );
        })}
        
        {successful_count > 0 && (
          <Card title="批量下载" style={{ marginTop: 16 }} size="small">
            <Button 
              icon={<DownloadOutlined />} 
              onClick={() => downloadAllPrimers(results)}
            >
              下载所有引物序列
            </Button>
          </Card>
        )}
      </div>
    );
  }

  // 处理传统格式数据
  if (legacyResults.length > 0) {
    return (
      <div>
        {showTitle && <Title level={4}>{title}</Title>}
        
        {legacyResults.map((result, index) => (
          <Card 
            key={result.sequence_id || index}
            title={`序列: ${result.sequence_id} (${result.sequence_length}bp)`}
            style={{ marginBottom: 16 }}
            size="small"
          >
            {result.success ? (
              <div>
                <Row gutter={16}>
                  <Col span={12}>
                    {result.forward_primer && renderPrimerCard(result.forward_primer, '正向引物 (Forward)', '#1890ff')}
                  </Col>
                  <Col span={12}>
                    {result.reverse_primer && renderPrimerCard(result.reverse_primer, '反向引物 (Reverse)', '#52c41a')}
                  </Col>
                </Row>
                
                {(result.tm_difference !== undefined || result.amplicon_length !== undefined) && (
                  <Card title="引物对评估" style={{ marginTop: 16 }} size="small">
                    <Descriptions column={2}>
                      {result.tm_difference !== undefined && (
                        <Descriptions.Item label="Tm差异">
                          <Tag color={result.tm_difference < 3 ? 'green' : 'orange'}>
                            {safeNumber(result.tm_difference, 1)}°C
                          </Tag>
                        </Descriptions.Item>
                      )}
                      {result.amplicon_length !== undefined && (
                        <Descriptions.Item label="扩增产物长度">
                          {safeNumber(result.amplicon_length)} bp
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                )}
              </div>
            ) : (
              <Alert
                message="引物设计失败"
                description={safeRender(result.message, '未知错误')}
                type="error"
                showIcon
              />
            )}
          </Card>
        ))}
        
        <Card title="批量下载" style={{ marginTop: 16 }} size="small">
          <Button 
            icon={<DownloadOutlined />} 
            onClick={() => downloadAllPrimers(legacyResults)}
          >
            下载所有引物序列
          </Button>
        </Card>
      </div>
    );
  }

  // 无数据情况
  return (
    <div>
      {showTitle && <Title level={4}>{title}</Title>}
      <Alert
        type="info"
        message="无引物设计结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    </div>
  );
};

export default PrimerDesignResult;
